<template lang="pug">
.autoinvoicing-settings-form
  h4.mb-5 Auto invoicing
  AppToggleButtonGroup.enabled-switch(
    :items="enabledOptions"
    :selected="selectedValue"
    :disabled="!autoInvoicingVisible"
    @input="setValue($event)"
  )
  template(v-if="!autoInvoicingVisible")
    p.setting-description.small
      | Upgrade your plan to enable auto-invoicing
  template(v-else)
    p.setting-description.small(v-if="clientMode && organizationAutoInvoicingEnabled")
      | Auto-invoicing for the organization is enabled.&nbsp;
      a.clickable(:href="invoicesSettingsLink")
        | Adjust auto-invoice settings
    p.setting-description.small(v-else-if="clientMode && !organizationAutoInvoicingEnabled")
      a.clickable(:href="invoicesSettingsLink")
        | Set up auto-invoicing for all your clients at once.
    p.setting-description.small(v-if="!clientMode")
      | These settings will be applied to all clients unless explicitly disabled or custom settings are applied.
  .form-wrapper(
    v-if="!clientMode || enabled"
    :class="{ disabled }"
  )
    .row
      .col-lg-6.form-group
        label.control-label Amount based on
        .form-check
          input#radio-hourly.form-check-input(
            v-model="basedOnType"
            type="radio"
            value="0"
            name="amount-based-on"
            :disabled="disabled"
          )
          label.form-check-label(for="radio-hourly") Hourly
          AppTooltipIcon.ml-5(
            text-type="lighter"
            :message="`Based on time logged to ${projectLabel} assigned to this client`"
            placement="auto right"
          )
        .d-flex.align-items-center
          .form-check
            input#radio-fixed-price.form-check-input(
              v-model="basedOnType"
              type="radio"
              value="1"
              name="amount-based-on"
              :disabled="disabled"
            )
            label.form-check-label(for="radio-fixed-price") Fixed price
          ValidationProvider(
            v-slot="{ errors }"
            :rules="`${requiredField}|greaterThanZero`"
          )
            .invoicing-fixed-amount(:class="{ 'has-error': errors[0] }")
              .input-group.input-group-sm.ml-15.col-lg-10
                input.form-control.px-5(
                  v-model="amountFixed"
                  :disabled="+basedOnType === invoicingTypePerHour || disabled"
                )
                span.input-group-addon {{ currency }}
              AppDialogError(:errors="errors")
      .col-lg-6.form-group
        label.control-label(for="frequency-select") Frequency
        .clearfix
          AppSelect#frequency-select(
            v-model="frequency"
            :options="frequencyOptions"
            :select2-options="defaultSelect2Options"
            :disabled="disabled"
          )
    .row.biweekly-period-start(v-if="isBiweeklyFrequency")
      .col-sm-12.form-group
        label.control-label Period starts on
        .btn-group-spaced
          AppButton.btn-sm.btn-default.btn-rounded(
            v-for="day in availablePeriodStartDays"
            :key="day.toISOString()"
            :class="{ active: isPeriodStartDaySelected(day) }"
            @click="selectPeriodStart(day)"
          )
            | {{ $filters.formatDate(day) }}
        span.help-block
        //- ^ this is to equalize spacing between the other fields above and below
    .row
      ValidationProvider(
        v-slot="{ errors }"
        :rules="`numeric|max_value:${delayMaxValue}`"
      )
        .form-group.col-lg-6(:class="{ 'has-error': errors[0] }")
          label.control-label(for="delay-sending-input") Delay Sending
          AppTooltipIcon.ml-5(
            text-type="lighter"
            message="This will delay your invoices from being sent immediately so you can edit any drafts that were created and waiting to send"
            placement="auto right"
          )
          .input-group
            input#delay-sending-input.form-control(
              v-model="delay"
              placeholder="No delay"
              :disabled="disabled"
            )
            span.input-group-addon days
          AppDialogError(:errors="errors")
      ValidationProvider(
        v-slot="{ errors }"
        :rules="`numeric|max_value:${reminderMaxValue}`"
      )
        .form-group.col-lg-6(:class="{ 'has-error': errors[0] }")
          label.control-label(for="reminder-input") Send reminder to pay after due
          AppTooltipIcon.text-lighter.ml-5(
            text-type="lighter"
            message="This will send a friendly reminder email after the due date, letting your client know that the invoice is still unpaid, and overdue"
            placement="auto right"
          )
          .input-group
            input#reminder-input.form-control(
              v-model="reminder"
              placeholder="No reminder"
              :disabled="disabled"
            )
            span.input-group-addon days
          AppDialogError(:errors="errors")
    .form-group
      label.control-label(for="line-items-select") Line items
      .clearfix
        AppSelect#line-items-select.col-lg-8.px-0(
          v-model="lineItems"
          :options="lineItemsForSelect"
          :select2-options="defaultSelect2Options"
          :disabled="disabled"
        )
    .form-group
      div
        AppSwitch.include-non-billable-switch(
          v-model="includeNonBillable"
          label="Include non-billable time"
          :disabled="disabled"
        )
      div
        AppSwitch.include-expenses-switch(
          v-model="includeExpenses"
          label="Include expenses"
          :disabled="disabled"
        )
</template>

<script>
import { isEmpty } from 'lodash-es';
import { mapGetters, mapState } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';
import { formatInternalDate } from '~/utils/time_utils';
import { UPDATE_AUTO_INVOICING } from '../../stores/InvoicesSettingsStore/types';
import { getProjectFlavourLabel } from '~/utils/project_flavour';
import {
  INVOICING_FREQUENCY_BIWEEKLY,
  INVOICING_TYPE_PER_HOUR,
  INVOICING_TYPE_FIXED,
  DELAY_MAX_VALUE,
  REMINDER_MAX_VALUE,
} from '~/app_constants';
import { OrganizationsResource } from '../../resources';

export const ENABLED_TOGGLE_OPTIONS = [
  { id: `automatic-invoicing-off`, text: 'Off', value: false },
  { id: `automatic-invoicing-org`, text: 'Org settings', value: null },
  { id: `automatic-invoicing-on`, text: 'Custom', value: true },
];

export default {
  name: 'AutoInvoicingSettingsForm',
  props: {
    clientMode: {
      type: Boolean,
      default: false,
    },
    newForm: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultSelect2Options: {
        allowClear: false,
        multiple: false,
        width: '100%',
        minimumResultsForSearch: -1,
      },
      invoicingTypePerHour: INVOICING_TYPE_PER_HOUR,
      invoicingTypeFixed: INVOICING_TYPE_FIXED,
      delayMaxValue: DELAY_MAX_VALUE,
      reminderMaxValue: REMINDER_MAX_VALUE,
      availablePeriodStartDays: null,
    };
  },
  computed: {
    ...mapState({
      projectFlavour: (state) => state.project_flavour,
      frequencyOptions: (state) => state.frequency_options,
      currency: (state) => state.organization.currency.currency,
      organizationId: (state) => state.organization.id,
      organizationAutoInvoicingEnabled: (state) => state.organization.auto_invoicing_enabled,
      automaticInvoicingEnabled: (state) => state.automatic_invoicing && state.automatic_invoicing.enabled,
      startWeekOn: (state) => state.organization.start_week_on,
      timeZone: (state) => state.organization.time_zone_id,
    }),
    ...mapGetters(['lineItemsForSelect', 'autoInvoicingVisible']),
    ...mapFormFields(
      'automatic_invoicing',
      {
        enabled: 'enabled',
        delay: 'delay',
        basedOnType: 'based_on.type',
        amountFixed: 'based_on.amount',
        periodStartsOn: 'period_starts_on',
        frequency: 'frequency',
        reminder: 'reminder',
        lineItems: 'line_items',
        includeNonBillable: 'include_non_billable',
        includeExpenses: 'include_expenses',
      },
      UPDATE_AUTO_INVOICING,
    ),
    projectLabel() {
      return getProjectFlavourLabel(this.projectFlavour.sym).toLowerCase();
    },
    selectedValue() {
      if (this.newForm) {
        return this.organizationAutoInvoicingEnabled || this.automaticInvoicingEnabled ? this.enabled : false;
      }

      return this.autoInvoicingVisible ? this.enabled : false;
    },
    disabled() {
      return !this.autoInvoicingVisible || !this.enabled;
    },
    requiredField() {
      return String(this.basedOnType) === String(this.invoicingTypeFixed) ? 'required' : '';
    },
    enabledOptions() {
      if (this.clientMode) {
        if (this.organizationAutoInvoicingEnabled) {
          return ENABLED_TOGGLE_OPTIONS;
        }

        return ENABLED_TOGGLE_OPTIONS.filter((opt) => opt.id !== 'automatic-invoicing-org');
      }
      ENABLED_TOGGLE_OPTIONS.filter((opt) => opt.id !== 'automatic-invoicing-org');

      return [
        { id: `automatic-invoicing-off`, text: 'Off', value: false },
        { id: `automatic-invoicing-on`, text: 'On', value: true },
      ];
    },
    invoicesSettingsLink() {
      return OrganizationsResource.invoicesSettingsLink({ id: this.organizationId });
    },
    isBiweeklyFrequency() {
      return parseInt(this.frequency) === INVOICING_FREQUENCY_BIWEEKLY;
    },
    periodStartsOnDate() {
      let periodStartsOn;

      if (this.periodStartsOn) {
        periodStartsOn = moment.tz(this.periodStartsOn, this.timeZone);
      }

      return periodStartsOn;
    },
  },
  watch: {
    frequency() {
      this.ensurePeriodStartsOn();
    },
    enabled(newValue, oldValue) {
      if (
        newValue &&
        !oldValue &&
        this.periodStartsOnDate &&
        this.periodStartsOnDate.isBefore(this.availablePeriodStartDays[0])
      ) {
        this.selectPeriodStart(this.availablePeriodStartDays[0]);
      }
    },
  },
  created() {
    const now = moment.tz(this.timeZone).startOf('day');
    const start = now.clone().subtract(1, 'week');
    const daysToSubtract = start.isoWeekday() - 1 - this.startWeekOn;

    start.subtract(daysToSubtract, 'days');
    const stop = start.clone().add(1, 'week');
    const availableDays = [start, stop];

    // If we have selected a date that is not expected, ensure that it is part of the available days
    if (this.periodStartsOnDate) {
      const existingChoice = availableDays.find((day) => day.isSame(this.periodStartsOnDate, 'day'));

      if (!existingChoice && this.periodStartsOnDate.isAfter(start)) {
        availableDays.push(this.periodStartsOnDate);
      }
    }

    availableDays.sort((first, second) => first.valueOf() - second.valueOf());

    this.availablePeriodStartDays = availableDays;

    this.ensurePeriodStartsOn();
  },
  methods: {
    selectPeriodStart(day) {
      this.periodStartsOn = formatInternalDate(day);
    },
    isPeriodStartDaySelected(day) {
      return this.periodStartsOn === formatInternalDate(day);
    },
    ensurePeriodStartsOn() {
      if (this.isBiweeklyFrequency && isEmpty(this.periodStartsOn)) {
        this.selectPeriodStart(this.availablePeriodStartDays[0]);
      }
    },
    setValue(value) {
      this.enabled = value;
    },
  },
};
</script>
